/** Venuefy marketing site — Phase 6 productization.
 *  Premium SaaS homepage (Linear/Vercel/Stripe inspired). Dark, typographic,
 *  gradient-accented. Completely independent of the wedding motif system.
 *  The wedding app lives untouched at /riyazandaimee (+ existing staff routes).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, PlayCircle, Radio, MessageSquare, FileSpreadsheet, Map,
  Users, Mic2, ClipboardList, ChefHat, Scissors, BarChart3, Megaphone,
  CalendarDays, Camera, Gamepad2, Armchair, Video, ConciergeBell,
  MonitorPlay, Bell, Zap, Languages, LayoutDashboard, Check, Sparkles,
  Globe, KeyRound, DownloadCloud,
} from "lucide-react";
import VenuefyMark from "../components/VenuefyMark";
import { T, useI18n } from "../lib/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const EVENT_TYPES = ["Wedding", "Corporate Event", "Conference", "Birthday", "Festival",
  "Fundraiser", "Charity Gala", "Holiday Party", "School Event", "Community Event", "Other"];
const GUEST_SIZES = ["Under 50", "50–150", "150–300", "300–1,000", "1,000+"];

function SectionLabel({ children }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-indigo-400 mb-4">
      <span className="h-px w-6 bg-indigo-400/60" /> {children}
    </div>
  );
}

export default function MarketingHome() {
  useI18n();
  const scrollToWaitlist = () => document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#08090c] text-zinc-200 antialiased" style={{ fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif" }} data-testid="marketing-home">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#08090c]/70 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="mk-logo">
            <VenuefyMark size={34} left="#e4e4ee" right="#8b5cf6" bars="#a78bfa" />
            <div className="leading-none text-center mt-1.5">
              <div className="font-semibold tracking-tight text-white text-[17px]">Venuefy</div>
              <div className="text-[8px] font-semibold tracking-[0.3em] text-violet-400 mt-1">THE EVENT OS</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-[13px] text-zinc-400">
            <a href="#platform" className="hover:text-white transition">Platform</a>
            <a href="#usecases" className="hover:text-white transition">Use Cases</a>
            <a href="#features" className="hover:text-white transition">Features</a>
            <a href="#roadmap" className="hover:text-white transition">Roadmap</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/riyazandaimee" className="text-[13px] text-zinc-400 hover:text-white transition" data-testid="mk-live-demo-link">{T("Live Event")}</Link>
            <button onClick={scrollToWaitlist} className="text-[13px] font-medium bg-white text-black px-4 py-1.5 rounded-lg hover:bg-zinc-200 transition" data-testid="mk-nav-waitlist">
              {T("Join the Waitlist")}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full opacity-25 blur-[120px]" style={{ background: "radial-gradient(closest-side, #6366f1, transparent)" }} />
          <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "56px 56px", maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%)" }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-20 text-center">
          <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 mb-8">
            <Sparkles className="w-3.5 h-3.5 text-violet-400" /> THE EVENT OS — now running live weddings
          </div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white leading-[1.05]" data-testid="mk-hero-headline">
            {T("Your Event.")}<br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-violet-400 to-fuchsia-400">{T("One Operating System.")}</span>
          </h1>
          <p className="mt-7 max-w-2xl mx-auto text-zinc-400 text-base md:text-lg leading-relaxed">
            {T("Venuefy replaces spreadsheets, WhatsApp groups, radios, paper schedules, seating charts, standalone RSVP apps, DJ software, and vendor coordination with one real-time platform connecting guests, hosts, planners, vendors, coordinators, MCs, and staff.")}
          </p>
          <div className="mt-8 flex items-center justify-center gap-2 flex-wrap" data-testid="mk-browser-only">
            {[[DownloadCloud, "No app to download"], [KeyRound, "No passwords to remember"], [Globe, "100% browser-based"]].map(([Icon, t]) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/25 text-violet-200">
                <Icon className="w-3.5 h-3.5" /> {t}
              </span>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-center gap-4">
            <button onClick={scrollToWaitlist} className="group inline-flex items-center gap-2 bg-white text-black font-medium px-6 py-3 rounded-xl hover:bg-zinc-200 transition" data-testid="mk-hero-cta">
              Join the Waitlist <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
            </button>
            <button className="inline-flex items-center gap-2 text-zinc-300 border border-white/15 px-6 py-3 rounded-xl hover:bg-white/5 transition" data-testid="mk-hero-demo" title="Demo video coming soon">
              <PlayCircle className="w-4 h-4" /> Watch Demo
            </button>
          </div>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/10 max-w-3xl mx-auto">
            {[["1", "platform"], ["10+", "connected roles"], ["Real-time", "everything"], ["0", "apps to install"]].map(([v, l]) => (
              <div key={l} className="bg-[#0b0d12] px-6 py-5">
                <div className="text-2xl font-semibold text-white">{v}</div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="max-w-6xl mx-auto px-6 py-24 border-t border-white/5">
        <SectionLabel>{T("The Problem")}</SectionLabel>
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white leading-tight">
              Live events still run on chaos.
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Behind every polished event is a mess of WhatsApp threads, spreadsheets, radios, printed
              schedules, paper seating charts, separate RSVP systems, multiple vendor apps, and hours of
              manual coordination. Information lives everywhere — and nowhere.
            </p>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Venuefy replaces all of it with one browser-based platform. Every guest, vendor, and staff
              member sees the same live state of the event — schedules, requests, queues, maps, games,
              and announcements — updated in real time. No installs. No radios. No paper.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[[MessageSquare, "WhatsApp groups"], [FileSpreadsheet, "Spreadsheets"], [Radio, "Radios"],
              [CalendarDays, "Printed schedules"], [Armchair, "Paper seating charts"], [ClipboardList, "Separate RSVP tools"],
              [LayoutDashboard, "Multiple vendor apps"], [Users, "Manual coordination"]].map(([Icon, label]) => (
              <div key={label} className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3.5">
                <Icon className="w-4 h-4 text-zinc-500" />
                <span className="text-sm text-zinc-400 line-through decoration-zinc-600">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where Venuefy sits */}
      <section className="max-w-6xl mx-auto px-6 py-24 border-t border-white/5" data-testid="mk-where-venuefy-sits">
        <SectionLabel>{T("Where Venuefy Sits")}</SectionLabel>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white leading-tight">
              Venuefy begins where <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400">everyone else ends.</span>
            </h2>
            <div className="mt-8 space-y-1 text-xl md:text-2xl font-medium">
              <div className="text-zinc-500">Current products help you <span className="line-through decoration-zinc-600">plan</span>.</div>
              <div className="text-white">Venuefy <span className="text-violet-400">runs the event</span>.</div>
            </div>
          </div>
          <div className="rounded-2xl border border-violet-500/25 bg-gradient-to-b from-violet-500/10 to-transparent p-8">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-400 mb-4">Think about your wedding</div>
            <p className="text-2xl md:text-3xl font-semibold text-white leading-snug">
              Guests aren{'\u2019'}t opening Zola while they{'\u2019'}re eating dinner.
            </p>
            <p className="mt-3 text-2xl md:text-3xl font-semibold leading-snug bg-clip-text text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400">
              They{'\u2019'}re using Venuefy.
            </p>
            <p className="mt-6 text-sm text-zinc-400 leading-relaxed">
              Straight from any phone browser — no app to download, no account to create, no password to remember.
            </p>
          </div>
        </div>
      </section>

      {/* The current market */}
      <section className="max-w-6xl mx-auto px-6 py-24 border-t border-white/5" data-testid="mk-current-market">
        <SectionLabel>{T("The Current Market")}</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">There are basically 5 categories.</h2>
        <div className="mt-10 rounded-2xl border border-white/8 overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-white/[0.04] text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="px-5 py-3.5 font-semibold">Category</th>
                <th className="px-5 py-3.5 font-semibold hidden sm:table-cell">Companies</th>
                <th className="px-5 py-3.5 font-semibold">What they do</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {[
                ["Wedding websites", "The Knot, Zola, Joy, Minted", "RSVP, registry, website"],
                ["Event registration", "Eventbrite, Splash", "Tickets and check-in"],
                ["Guest engagement", "WedShoots, POV, GuestPix", "Photos and galleries"],
                ["Planner software", "Aisle Planner, Planning Pod", "Vendor contracts, budgets"],
                ["Event production", "Cvent, Bizzabo, Whova", "Conferences and corporate events"],
              ].map(([cat, cos, what]) => (
                <tr key={cat} className="border-t border-white/5">
                  <td className="px-5 py-4 font-medium text-white">{cat}</td>
                  <td className="px-5 py-4 text-zinc-400 hidden sm:table-cell">{cos}</td>
                  <td className="px-5 py-4 text-zinc-400">{what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-10 text-center">
          <div className="text-zinc-400 text-lg">Notice something{'\u2026'}</div>
          <div className="mt-3 text-2xl md:text-4xl font-bold text-white">Nobody owns the actual event itself.</div>
          <div className="mt-2 text-lg text-zinc-400">Everyone stops once guests arrive.</div>
          <div className="mt-6 inline-flex items-center gap-2 text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400">
            Venuefy starts there. <ArrowRight className="w-5 h-5 text-fuchsia-400" />
          </div>
        </div>
      </section>

      {/* Event OS */}
      <section id="platform" className="max-w-6xl mx-auto px-6 py-24 border-t border-white/5">
        <SectionLabel>{T("What is an Event Operating System?")}</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white leading-tight max-w-2xl">
          One platform. Every participant. The same live truth.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Users, role: "Guests", tone: "from-indigo-500/20", items: ["Schedule", "Venue Map", "Live Feed", "Games", "Seating", "Livestream", "Requests"] },
            { icon: Mic2, role: "MC", tone: "from-violet-500/20", items: ["Agenda", "Projector", "Live Games", "Polls", "Winner Reveals"] },
            { icon: ClipboardList, role: "Coordinator", tone: "from-fuchsia-500/20", items: ["Request Management", "Staff Coordination", "Alerts", "Operations Dashboard"] },
            { icon: ChefHat, role: "Kitchen", tone: "from-amber-500/20", items: ["Food Queue"] },
            { icon: Scissors, role: "Embroidery", tone: "from-emerald-500/20", items: ["Orders", "Inventory"] },
            { icon: BarChart3, role: "Hosts", tone: "from-sky-500/20", items: ["Broadcasts", "Analytics", "Reports", "Administration"] },
          ].map(({ icon: Icon, role, tone, items }) => (
            <div key={role} className={`rounded-2xl border border-white/8 bg-gradient-to-b ${tone} to-transparent p-6`} data-testid={`mk-role-${role.toLowerCase()}`}>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-lg bg-white/8 flex items-center justify-center"><Icon className="w-4 h-4 text-white" /></div>
                <div className="font-semibold text-white">{role}</div>
              </div>
              <ul className="space-y-2">
                {items.map((it) => (
                  <li key={it} className="flex items-center gap-2 text-sm text-zinc-400">
                    <Check className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Use cases */}
      <section id="usecases" className="max-w-6xl mx-auto px-6 py-24 border-t border-white/5">
        <SectionLabel>{T("Use Cases")}</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Built for every gathering.</h2>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-3">
          {["Wedding", "Corporate Event", "Conference", "Birthday", "Festival",
            "Fundraiser", "Charity Gala", "Holiday Party", "School Event", "Community Event"].map((u) => (
            <div key={u} className="rounded-xl border border-white/8 bg-white/[0.03] px-4 py-6 text-center hover:border-indigo-500/40 hover:bg-indigo-500/5 transition" data-testid={`mk-usecase-${u.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="text-sm font-medium text-zinc-200">{u}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-24 border-t border-white/5">
        <SectionLabel>{T("Features")}</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Everything an event needs. Nothing it doesn{'\u2019'}t.</h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            [Map, "Interactive Venue Maps", "Tap any dish, station, or vendor to see exactly where it lives on the floor plan."],
            [ConciergeBell, "Vendor Queues", "Photo booths, embroidery, kitchens — every vendor runs a live, fair queue."],
            [ClipboardList, "Guest Requests", "Food, help, and song requests flow straight to the right staff dashboards."],
            [Camera, "Live Feed", "Guests post photos and messages that appear instantly across the venue."],
            [Video, "Digital Photo Book", "Every photo of the night, collected automatically into one shareable book."],
            [Gamepad2, "Games", "Crosswords, live polls, and audience games projected in real time."],
            [LayoutDashboard, "Operations Dashboard", "Datadog-grade health, activity, and readiness for the people running the show."],
            [MonitorPlay, "Projector Mode", "A broadcast-quality display surface for announcements, games, and reveals."],
            [Mic2, "MC Mode", "Run the whole show — agenda, games, winner reveals — from one screen."],
            [BarChart3, "Analytics", "Understand engagement across every feature while the event is still running."],
            [Bell, "Live Notifications", "Push alerts reach staff and guests the moment something needs attention."],
            [Zap, "Real-time Updates", "WebSocket-powered. Every screen updates in under a second."],
            [Languages, "Multi-language", "Serve international guest lists in their own language."],
            [Megaphone, "Vendor Dashboards", "Purpose-built consoles for kitchens, DJs, photographers, and more."],
          ].map(([Icon, title, desc]) => (
            <div key={title} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 hover:bg-white/[0.04] transition" data-testid={`mk-feature-${title.replace(/\s+/g, "-").toLowerCase()}`}>
              <Icon className="w-5 h-5 text-indigo-400 mb-3" />
              <div className="font-semibold text-white text-[15px]">{title}</div>
              <div className="text-sm text-zinc-500 mt-1.5 leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Roadmap */}
      <section id="roadmap" className="max-w-6xl mx-auto px-6 py-24 border-t border-white/5">
        <SectionLabel>{T("Roadmap")}</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Where Venuefy is going.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { phase: "Now", tone: "border-indigo-500/50", badge: "bg-indigo-500/15 text-indigo-300", items: ["Wedding OS"] },
            { phase: "Soon", tone: "border-white/10", badge: "bg-white/8 text-zinc-300", items: ["Corporate Events", "Venue Platform"] },
            { phase: "Enterprise", tone: "border-white/10", badge: "bg-white/8 text-zinc-300", items: ["Venue Management", "Public APIs"] },
          ].map(({ phase, tone, badge, items }) => (
            <div key={phase} className={`rounded-2xl border ${tone} bg-white/[0.02] p-6`} data-testid={`mk-roadmap-${phase.toLowerCase()}`}>
              <span className={`text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${badge}`}>{phase}</span>
              <ul className="mt-5 space-y-3">
                {items.map((it) => (
                  <li key={it} className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Waitlist */}
      <section id="waitlist" className="border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-20 blur-[120px]" style={{ background: "radial-gradient(closest-side, #8b5cf6, transparent)" }} />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 py-24">
          <div className="text-center mb-10">
            <SectionLabel>{T("Early Access")}</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Join the Waitlist</h2>
            <p className="mt-3 text-zinc-400">{T("Be first in line when Venuefy opens for your events.")}</p>
          </div>
          <WaitlistForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-3">
              <VenuefyMark size={28} left="#e4e4ee" right="#8b5cf6" bars="#a78bfa" />
              <div className="leading-none text-center mt-1">
                <div className="font-semibold text-white">Venuefy</div>
                <div className="text-[7px] font-semibold tracking-[0.3em] text-violet-400 mt-0.5">THE EVENT OS</div>
              </div>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed">The Operating System for Live Events. Browser-based. No apps. No passwords.</p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">Product</div>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li><a href="#platform" className="hover:text-white transition">Platform</a></li>
              <li><a href="#features" className="hover:text-white transition">Features</a></li>
              <li><a href="#roadmap" className="hover:text-white transition">Roadmap</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">Live</div>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li><Link to="/riyazandaimee" className="hover:text-white transition" data-testid="mk-footer-wedding">Riyaz & Aimee</Link></li>
              <li><Link to="/vendor" className="hover:text-white transition">{T("Vendor sign in")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">Company</div>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li><a href="#waitlist" className="hover:text-white transition">Early Access</a></li>
              <li><span className="text-zinc-600">Contact — coming soon</span></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between text-xs text-zinc-600">
            <span>© {new Date().getFullYear()} Venuefy. All rights reserved.</span>
            <span>Built for one perfect night — and every one after.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-zinc-400">{label}{required && <span className="text-indigo-400"> *</span>}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/60 focus:bg-white/[0.06] transition";

function WaitlistForm() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", company: "", event_type: "", estimated_guests: "", comments: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // null | "new" | "dup"
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/waitlist`, form);
      setDone(data.duplicate ? "dup" : "new");
    } catch (err) {
      alert(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-indigo-500/30 bg-indigo-500/5 p-10 text-center" data-testid="mk-waitlist-success">
        <div className="w-12 h-12 rounded-full bg-indigo-500/15 flex items-center justify-center mx-auto mb-4">
          <Check className="w-6 h-6 text-indigo-400" />
        </div>
        <div className="text-xl font-semibold text-white">{done === "dup" ? "You're already on the list" : "You're on the list"}</div>
        <div className="text-sm text-zinc-400 mt-2">We{'\u2019'}ll reach out as soon as Venuefy opens for your events.</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.02] p-7 space-y-4" data-testid="mk-waitlist-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={T("First Name")} required>
          <input className={inputCls} value={form.first_name} onChange={set("first_name")} required maxLength={100} data-testid="wl-first-name" />
        </Field>
        <Field label={T("Last Name")} required>
          <input className={inputCls} value={form.last_name} onChange={set("last_name")} required maxLength={100} data-testid="wl-last-name" />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={T("Email")} required>
          <input type="email" className={inputCls} value={form.email} onChange={set("email")} required maxLength={200} data-testid="wl-email" />
        </Field>
        <Field label={T("Company (optional)")}>
          <input className={inputCls} value={form.company} onChange={set("company")} maxLength={200} data-testid="wl-company" />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={T("Type of Event")}>
          <select className={inputCls} value={form.event_type} onChange={set("event_type")} data-testid="wl-event-type">
            <option value="" className="bg-[#0b0d12]">Select…</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t} className="bg-[#0b0d12]">{t}</option>)}
          </select>
        </Field>
        <Field label={T("Estimated Guests")}>
          <select className={inputCls} value={form.estimated_guests} onChange={set("estimated_guests")} data-testid="wl-guests">
            <option value="" className="bg-[#0b0d12]">Select…</option>
            {GUEST_SIZES.map((t) => <option key={t} value={t} className="bg-[#0b0d12]">{t}</option>)}
          </select>
        </Field>
      </div>
      <Field label={T("Comments")}>
        <textarea className={`${inputCls} min-h-[90px] resize-y`} value={form.comments} onChange={set("comments")} maxLength={2000} placeholder={T("Tell us about your event…")} data-testid="wl-comments" />
      </Field>
      <button type="submit" disabled={busy} className="w-full bg-white text-black font-medium py-3 rounded-xl hover:bg-zinc-200 transition disabled:opacity-60" data-testid="wl-submit">
        {busy ? T("Joining…") : T("Join the Waitlist")}
      </button>
      <p className="text-[11px] text-zinc-600 text-center">No spam. We{'\u2019'}ll only email you about Venuefy availability.</p>
    </form>
  );
}
